import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { usePlans, useRouters } from '../api/queries';
import { useCurrentRouter } from '../lib/current-router';
import type { Paginated, PppoeClientDetail, PppoeClientDto, PppoeClientStatus } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { PppoeClientStatusBadge } from '../components/status';
import {
  Card,
  ConfirmDialog,
  CopyButton,
  Empty,
  ErrorAlert,
  Field,
  Modal,
  Pagination,
  Spinner,
} from '../components/ui';
import { formatDateTime, formatDuration, formatRelative, formatRemainingSeconds, fromDateTimeInput } from '../lib/format';

const STATUS_OPTIONS: { value: PppoeClientStatus | ''; label: string }[] = [
  { value: '', label: 'Todos los estados' },
  { value: 'ACTIVE', label: 'Activos' },
  { value: 'PENDING_SYNC', label: 'Pendientes en router' },
  { value: 'DISABLED', label: 'Suspendidos' },
  { value: 'EXPIRED', label: 'Cortados' },
  { value: 'DELETED', label: 'Eliminados' },
];

export function PppoeClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canOnRouter } = useAuth();
  const queryClient = useQueryClient();
  const current = useCurrentRouter();

  const routerId = current.routerId;
  const planId = searchParams.get('planId') ?? '';
  const status = (searchParams.get('status') ?? '') as PppoeClientStatus | '';
  const search = searchParams.get('search') ?? '';
  const page = Number(searchParams.get('page') ?? '1');

  const routers = useRouters();
  const plans = usePlans(routerId || undefined, false, 'PPPOE');

  // El filtro de plan es de un router concreto: al cambiar de router deja de tener sentido.
  const previousRouterId = useRef(routerId);
  useEffect(() => {
    if (previousRouterId.current === routerId) return;
    previousRouterId.current = routerId;
    const next = new URLSearchParams(searchParams);
    next.delete('planId');
    next.delete('page');
    setSearchParams(next, { replace: true });
  }, [routerId, searchParams, setSearchParams]);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<PppoeClientDto | null>(null);
  const [editing, setEditing] = useState<PppoeClientDto | null>(null);
  const [deleting, setDeleting] = useState<PppoeClientDto | null>(null);
  const [revealed, setRevealed] = useState<PppoeClientDetail | null>(null);
  const [pageError, setPageError] = useState<unknown>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Cambiar cualquier filtro invalida la página actual.
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const clients = useQuery({
    queryKey: ['pppoe-clients', { routerId, planId, status, search, page }],
    enabled: Boolean(routerId),
    queryFn: () =>
      api.get<Paginated<PppoeClientDto>>('/pppoe-clients', {
        routerId,
        planId: planId || undefined,
        status: status || undefined,
        search: search || undefined,
        page,
        pageSize: 25,
      }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['pppoe-clients'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const changeStatus = useMutation({
    mutationFn: ({ client, next }: { client: PppoeClientDto; next: 'ACTIVE' | 'DISABLED' }) =>
      api.patch<PppoeClientDto>(`/pppoe-clients/${client.id}/status`, { status: next }),
    onSuccess: invalidate,
    onError: (error) => setPageError(error),
  });

  const retrySync = useMutation({
    mutationFn: (client: PppoeClientDto) => api.post<PppoeClientDto>(`/pppoe-clients/${client.id}/retry-sync`),
    onSuccess: invalidate,
    onError: (error) => setPageError(error),
  });

  const remove = useMutation({
    mutationFn: (client: PppoeClientDto) => api.del<void>(`/pppoe-clients/${client.id}`),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: (error) => setPageError(error),
  });

  const reveal = useMutation({
    mutationFn: (client: PppoeClientDto) => api.get<PppoeClientDetail>(`/pppoe-clients/${client.id}`),
    onSuccess: (detail) => setRevealed(detail),
    onError: (error) => setPageError(error),
  });

  const canCreateSomewhere = (routers.data ?? []).some((router) => canOnRouter(router.id, 'create'));

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Clientes PPPoE</h1>
          <p>Clientes fijos del servicio PPPoE. Suspender corta el acceso sin perder el contrato.</p>
        </div>
        {canCreateSomewhere && (
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            Crear cliente
          </button>
        )}
      </div>

      <ErrorAlert error={pageError} onDismiss={() => setPageError(null)} />

      <div className="toolbar">
        <select value={planId} onChange={(event) => setParam('planId', event.target.value)} style={{ width: 'auto' }}>
          <option value="">Todos los planes</option>
          {(plans.data ?? []).map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </select>

        <select value={status} onChange={(event) => setParam('status', event.target.value)} style={{ width: 'auto' }}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <input
          value={search}
          onChange={(event) => setParam('search', event.target.value)}
          placeholder="Buscar cliente, comentario o IP…"
          style={{ width: 260 }}
        />

        {clients.isFetching && <Spinner label="" />}
      </div>

      <Card
        footer={
          clients.data && clients.data.meta.total > 0 ? (
            <Pagination
              page={clients.data.meta.page}
              totalPages={clients.data.meta.totalPages}
              total={clients.data.meta.total}
              onPage={(next) => setParam('page', String(next))}
            />
          ) : undefined
        }
      >
        {clients.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : clients.data && clients.data.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th>IP fija</th>
                  <th>Corte</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {clients.data.data.map((client) => {
                  const canWrite = canOnRouter(client.routerId, 'create');
                  const canDelete = canOnRouter(client.routerId, 'delete');

                  return (
                    <tr key={client.id}>
                      <td>
                        <strong className="mono">{client.username}</strong>
                        <div className="faint" style={{ fontSize: 12 }}>
                          {client.comment ||
                            (client.lastSeenAt
                              ? `visto ${formatRelative(client.lastSeenAt)}`
                              : 'nunca conectado')}
                        </div>
                      </td>
                      <td>{client.planName ?? <span className="faint">—</span>}</td>
                      <td>
                        <PppoeClientStatusBadge status={client.status} />
                        {client.syncError && (
                          <div className="faint" style={{ fontSize: 12, maxWidth: 240 }}>
                            {client.syncError}
                          </div>
                        )}
                      </td>
                      <td className="mono">{client.staticIp ?? <span className="faint">pool</span>}</td>
                      <td>{client.expiresAt ? formatRelative(client.expiresAt) : <span className="faint">—</span>}</td>
                      <td>{client.planValidityMinutes ? <div className="faint" style={{ fontSize: 12 }}>validez: {client.firstUsedAt && client.expiresAt ? formatRemainingSeconds((new Date(client.expiresAt).getTime() - Date.now()) / 1000) : 'sin iniciar'}</div> : client.expiresAt ? formatRemainingSeconds((new Date(client.expiresAt).getTime() - Date.now()) / 1000) : <span className="faint">—</span>}</td>
                      <td className="actions">
                        <button type="button" className="small ghost" onClick={() => reveal.mutate(client)}>
                          Ver
                        </button>

                        {canWrite && client.status !== 'DELETED' && (
                          <button
                            type="button"
                            className="small ghost"
                            style={{ marginLeft: 4 }}
                            onClick={() => setEditing(client)}
                          >
                            Editar
                          </button>
                        )}

                        {canWrite && client.status === 'PENDING_SYNC' && (
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 4 }}
                            disabled={retrySync.isPending}
                            onClick={() => retrySync.mutate(client)}
                          >
                            Reintentar
                          </button>
                        )}

                        {canWrite && client.status === 'ACTIVE' && (
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 4 }}
                            onClick={() => changeStatus.mutate({ client, next: 'DISABLED' })}
                          >
                            Suspender
                          </button>
                        )}

                        {canWrite && (client.status === 'DISABLED' || client.status === 'EXPIRED') && (
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 4 }}
                            onClick={() => changeStatus.mutate({ client, next: 'ACTIVE' })}
                          >
                            Reactivar
                          </button>
                        )}

                        {canDelete && client.status !== 'DELETED' && (
                          <button
                            type="button"
                            className="small danger"
                            style={{ marginLeft: 4 }}
                            onClick={() => setDeleting(client)}
                          >
                            Eliminar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No hay clientes que coincidan con los filtros.</Empty>
        )}
      </Card>

      {creating && (
        <ClientFormModal
          onClose={() => setCreating(false)}
          onSaved={(client) => {
            setCreating(false);
            setCreated(client);
            invalidate();
          }}
        />
      )}

      {editing && (
        <ClientFormModal
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}

      {created && <CredentialModal client={created} onClose={() => setCreated(null)} isNew />}
      {revealed && <CredentialModal client={revealed} live={revealed.live} onClose={() => setRevealed(null)} />}

      {deleting && (
        <ConfirmDialog
          title={`Eliminar ${deleting.username}`}
          danger
          busy={remove.isPending}
          confirmLabel="Eliminar del router"
          message={
            <p>
              Se borra el secreto del MikroTik y se corta su sesión si está conectado. La fila se
              conserva en MikroManager marcada como eliminada para no perder el historial. Si sólo
              es un impago, usa «Suspender»: es reversible.
            </p>
          }
          onCancel={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </main>
  );
}

function ClientFormModal({
  client,
  onClose,
  onSaved,
}: {
  client?: PppoeClientDto;
  onClose: () => void;
  onSaved: (client: PppoeClientDto) => void;
}) {
  const { canOnRouter } = useAuth();
  const routers = useRouters();
  const current = useCurrentRouter();
  const available = (routers.data ?? []).filter(
    (router) => router.isActive && canOnRouter(router.id, 'create'),
  );
  const isEdit = Boolean(client);

  // Un alta se abre sobre el router de trabajo para no crear por descuido en otro.
  const [routerId, setRouterId] = useState(
    () =>
      client?.routerId ??
      (available.some((router) => router.id === current.routerId) ? current.routerId : ''),
  );
  const [planId, setPlanId] = useState(client?.planId ?? '');
  const [username, setUsername] = useState(client?.username ?? '');
  const [password, setPassword] = useState('');
  const [comment, setComment] = useState(client?.comment ?? '');
  const [staticIp, setStaticIp] = useState(client?.staticIp ?? '');
  const [callerId, setCallerId] = useState(client?.callerId ?? '');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<unknown>(null);

  const plans = usePlans(routerId || undefined, false, 'PPPOE');

  const save = useMutation({
    mutationFn: () =>
      isEdit
        ? api.patch<PppoeClientDto>(`/pppoe-clients/${(client as PppoeClientDto).id}`, {
            planId: planId || undefined,
            password: password || undefined,
            comment,
            // Cadena vacía borra el valor en el router; es distinta de "no tocar".
            staticIp,
            callerId,
            expiresAt: fromDateTimeInput(expiresAt) ?? '',
          })
        : api.post<PppoeClientDto>('/pppoe-clients', {
            routerId,
            planId,
            username,
            password: password || undefined,
            comment: comment || undefined,
            staticIp: staticIp || undefined,
            callerId: callerId || undefined,
            expiresAt: fromDateTimeInput(expiresAt),
          }),
    onSuccess: onSaved,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      title={isEdit ? `Editar ${client?.username}` : 'Crear cliente PPPoE'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || (!isEdit && (!routerId || !planId || !username))}
            onClick={() => save.mutate()}
          >
            {save.isPending ? <Spinner label="Guardando…" /> : isEdit ? 'Guardar' : 'Crear cliente'}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {!isEdit && available.length === 0 ? (
        <p className="muted">
          No tienes permiso de creación en ningún router activo. Pide a un administrador que te
          asigne acceso.
        </p>
      ) : (
        <>
          {!isEdit && (
            <div className="form-row">
              <Field label="Router">
                <select
                  value={routerId}
                  onChange={(event) => {
                    setRouterId(event.target.value);
                    setPlanId('');
                  }}
                >
                  <option value="">Selecciona…</option>
                  {available.map((router) => (
                    <option key={router.id} value={router.id}>
                      {router.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Plan">
                <select value={planId} onChange={(event) => setPlanId(event.target.value)} disabled={!routerId}>
                  <option value="">Selecciona…</option>
                  {(plans.data ?? []).map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          {isEdit && (
            <Field label="Plan">
              <select value={planId ?? ''} onChange={(event) => setPlanId(event.target.value)}>
                <option value="">Sin plan</option>
                {/* Un plan global heredado ya no se lista, pero el cliente puede conservarlo. */}
                {client?.planId && !(plans.data ?? []).some((plan) => plan.id === client.planId) && (
                  <option value={client.planId}>{client.planName ?? 'Plan actual'} (actual)</option>
                )}
                {(plans.data ?? []).map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <div className="form-row">
            {!isEdit && (
              <Field label="Usuario" hint="El que se configura en el CPE del cliente">
                <input value={username} onChange={(event) => setUsername(event.target.value)} />
              </Field>
            )}
            <Field
              label={isEdit ? 'Nueva contraseña' : 'Contraseña'}
              hint={isEdit ? 'Vacío = no cambiar' : 'Vacío = se genera aleatoria'}
            >
              <input value={password} onChange={(event) => setPassword(event.target.value)} />
            </Field>
          </div>

          <Field label="Comentario">
            <input
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Nombre, dirección, referencia del contrato…"
            />
          </Field>

          <div className="form-row">
            <Field label="IP fija" hint="Vacío = la asigna el pool del perfil">
              <input
                value={staticIp}
                onChange={(event) => setStaticIp(event.target.value)}
                placeholder="172.16.0.50"
              />
            </Field>
            <Field label="MAC del CPE (caller-id)" hint="Vacío = se acepta desde cualquier equipo">
              <input
                value={callerId}
                onChange={(event) => setCallerId(event.target.value)}
                placeholder="AA:BB:CC:DD:EE:FF"
              />
            </Field>
          </div>

          <Field
            label="Fecha de corte"
            hint="Opcional. Llegada la fecha se suspende el servicio automáticamente (impago). Al cobrar, usa «Reactivar» y pon la fecha siguiente."
          >
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
          </Field>
        </>
      )}
    </Modal>
  );
}

function CredentialModal({
  client,
  live,
  onClose,
  isNew,
}: {
  client: PppoeClientDto;
  live?: PppoeClientDetail['live'];
  onClose: () => void;
  isNew?: boolean;
}) {
  return (
    <Modal
      title={isNew ? 'Cliente creado' : client.username}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={() => window.print()}>
            Imprimir
          </button>
          <button type="button" className="primary" onClick={onClose}>
            Listo
          </button>
        </>
      }
    >
      {client.status === 'PENDING_SYNC' && (
        <div className="alert warn">
          <strong>Guardado en MikroManager, pero todavía no en el router.</strong>
          <div style={{ marginTop: 4 }}>
            El cliente no podrá conectar hasta que se sincronice. Usa «Reintentar» cuando el router
            vuelva a responder.
          </div>
          {client.syncError && <div className="detail">{client.syncError}</div>}
        </div>
      )}

      <div className="credential-box">
        <div>
          <div className="stat-label">Usuario</div>
          <div className="value">{client.username}</div>
        </div>
        <CopyButton value={client.username} />
      </div>

      {client.password ? (
        <div className="credential-box">
          <div>
            <div className="stat-label">Contraseña</div>
            <div className="value">{client.password}</div>
          </div>
          <CopyButton value={client.password} />
        </div>
      ) : (
        <p className="muted">
          No tienes permiso de creación en este router, así que la contraseña no se muestra.
        </p>
      )}

      <table style={{ marginTop: 14 }}>
        <tbody>
          {live && (
            <tr>
              <th>Ahora mismo</th>
              <td>
                {live.error ? (
                  <span className="faint">router sin respuesta: {live.error}</span>
                ) : live.online ? (
                  <>
                    Conectado · {live.onlineAddress ?? '—'} ·{' '}
                    {formatDuration(live.onlineUptimeSeconds ?? 0)}
                  </>
                ) : (
                  'Desconectado'
                )}
              </td>
            </tr>
          )}
          <tr>
            <th>Plan</th>
            <td>{client.planName ?? '—'}</td>
          </tr>
          <tr>
            <th>Router</th>
            <td>{client.routerName ?? '—'}</td>
          </tr>
          <tr>
            <th>IP fija</th>
            <td>{client.staticIp ?? 'la asigna el pool'}</td>
          </tr>
          <tr>
            <th>MAC del CPE</th>
            <td>{client.callerId ?? 'cualquiera'}</td>
          </tr>
          <tr>
            <th>Primer uso</th>
            <td>{formatDateTime(client.firstUsedAt)}</td>
          </tr>
          <tr>
            <th>Visto por última vez</th>
            <td>{formatDateTime(client.lastSeenAt)}</td>
          </tr>
          <tr>
            <th>Fecha de corte</th>
            <td>{formatDateTime(client.expiresAt)}</td>
          </tr>
          {(live?.lastDisconnectReason ?? client.lastDisconnectReason) && (
            <tr>
              <th>Última desconexión</th>
              <td>{live?.lastDisconnectReason ?? client.lastDisconnectReason}</td>
            </tr>
          )}
          {client.password && (
            <tr>
              <th>Copiar ambos</th>
              <td>
                <CopyButton value={`${client.username} / ${client.password}`} label="Usuario y contraseña" />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Modal>
  );
}
